/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';
import { ensureRecaptchaScript } from './recaptcha-loader';

const WARMUP_EVENT = 'focusin';
const FIELD_SELECTOR = 'input, textarea, select';

/**
 * Starts loading the reCAPTCHA SDK the first time a person touches a form field.
 *
 * The SDK is no longer fetched on page load, which is the point -- the landing
 * page has no form and should not pay for it. But moving the download to the
 * moment a token is needed would move it onto the submit click, where the person
 * waits for it with the button spinning.
 *
 * Focusing a field is the earliest honest signal that a submit is coming, and it
 * buys the seconds of typing as download time. Deliberately fire-and-forget: a
 * failure here is not the user's problem, since the real token request retries
 * and surfaces its own error.
 */
export function useRecaptchaWarmup(): void {
  useEffect(() => {
    const onFirstFieldFocus = (event: Event) => {
      const target = event.target as Element | null;
      if (!target?.matches?.(FIELD_SELECTOR)) return;
      document.removeEventListener(WARMUP_EVENT, onFirstFieldFocus);
      ensureRecaptchaScript();
    };

    document.addEventListener(WARMUP_EVENT, onFirstFieldFocus);
    return () => document.removeEventListener(WARMUP_EVENT, onFirstFieldFocus);
  }, []);
}
